<script setup>
import { useUserStore } from '@/model/dataLayer/api/userStore'
import { ref } from '@vue/composition-api'
import LogoDisplay from '@/views/widget/uiComponent/LogoDisplay.vue'

const userStore = useUserStore()
const rememberEmail = ref(localStorage.getItem('rememberedEmail') || false)

// Save email to localStorage if remember is checked
const saveEmail = () => {
  if (rememberEmail.value) {
    localStorage.setItem('rememberedEmail', userStore.emailInput.value)
  } else {
    localStorage.removeItem('rememberedEmail')
  }
}
</script>

<template>
  <div
    class="login-page d-flex align-center justify-center"
    style="height: 100vh; background-color: #f5f5f5;"
  >
    <v-card
      class="login-card pa-8 d-flex flex-column justify-center align-center text-center"
      elevation="10"
      max-width="450px"
      width="90%"
    >
      <div class="mb-6">
        <LogoDisplay
          size="large"
          :with-version="true"
        />
      </div>

      <div
        v-if="userStore.userNotExists.value"
        class="text-h5 font-weight-bold primary--text mb-4"
      >
        Error
      </div>
      <div
        v-else
        class="text-h5 font-weight-bold primary--text mb-4"
      >
        Welcome to Aaden POS
      </div>

      <template v-if="userStore.userNotExists.value">
        <v-alert
          type="error"
          class="mb-4 text-body-2"
          dense
        >
          Sorry, your email address does not have access permission.
        </v-alert>
        <v-btn
          class="mt-2 primary"
          elevation="2"
          rounded
          @click="userStore.reset()"
        >
          Back
        </v-btn>
      </template>

      <template v-else-if="userStore.otpSent.value">
        <v-alert
          type="info"
          class="mb-4 text-body-2"
          dense
        >
          Please enter the 6-digit code we just sent to {{ userStore.emailInput.value }}.
          This code is valid for 60 minutes. If you entered the wrong email address,
          click the "Back" button.
        </v-alert>

        <div class="otp-container my-6">
          <v-otp-input
            v-model="userStore.otpInput.value"
            :error="userStore.otpError.value"
            :loading="userStore.loading.value"
            autofocus
            @finish="userStore.login()"
          />
        </div>

        <v-btn
          block
          class="primary mt-4"
          rounded
          elevation="2"
          @click="userStore.login()"
          :loading="userStore.loading.value"
          :disabled="userStore.otpInput.value.length < 6"
        >
          Verify
        </v-btn>

        <v-btn
          block
          class="mt-4"
          text
          @click="userStore.reset()"
        >
          Back
        </v-btn>
      </template>

      <template v-else>
        <div class="text-body-2 mb-4 text--secondary">
          Enter your email address to receive a verification code
        </div>

        <v-alert
          v-if="userStore.otpError"
          type="error"
          class="mb-4 text-body-2"
          dense
        >
          {{ typeof userStore.otpError === 'string' ? userStore.otpError : 'An error occurred. Please try again.' }}
        </v-alert>

        <div style="width:100%">
          <v-form v-model="userStore.formReady.value">
            <v-text-field
              v-model="userStore.emailInput.value"
              :rules="[userStore.validateEmail]"
              label="Email Address"
              placeholder="youremail@example.com"
              prepend-inner-icon="mdi-email-outline"
              style="width: 100%"
              validate-on="input eager"
              outlined
              dense
              autofocus
              @keyup.enter="saveEmail(); userStore.sendOTP()"
            />

            <v-checkbox
              v-model="rememberEmail"
              label="Remember my email"
              class="mt-0 mb-4"
              hide-details
              dense
            />
          </v-form>
        </div>

        <v-btn
          :disabled="!userStore.formReady.value"
          :loading="userStore.loading.value"
          block
          class="primary"
          rounded
          elevation="2"
          height="44"
          @click="saveEmail(); userStore.sendOTP()"
        >
          Send Verification Code
        </v-btn>
      </template>

      <div class="mt-6 text-caption text--secondary">
        Copyright © 2023-2024 Aaden POS. All rights reserved.
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.login-page {
  background-image: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.login-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.login-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1) !important;
}

.otp-container {
  width: 100%;
}
</style>
